import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { isIOS, isStandalone } from "@/lib/pwa";
import { useLang } from "@/context/LanguageContext";

const toKey = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const ParentAlerts = ({ creds }) => {
  const { L } = useLang();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (supported()) navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then(setSub).catch(() => {});
  }, []);

  const iosNeedsInstall = isIOS() && !isStandalone();
  const on = async () => {
    setBusy(true);
    try {
      if ((await Notification.requestPermission()) !== "granted") { toast.error(L("Please allow notifications in your browser settings.", "कृपया ब्राउज़र सेटिंग में सूचनाएँ चालू करें।")); return; }
      const reg = await navigator.serviceWorker.register("/sw.js").then(() => navigator.serviceWorker.ready);
      const { data } = await api.get("/push/key");
      const s = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(data.public_key) });
      await api.post("/push/subscribe", { ...creds, subscription: s.toJSON() });
      setSub(s);
      toast.success(L("Alerts are on — you'll get a test notification now.", "अलर्ट चालू — अभी एक टेस्ट सूचना आएगी।"));
    } catch { toast.error(L("Could not turn on alerts on this device.", "इस डिवाइस पर अलर्ट चालू नहीं हो सके।")); } finally { setBusy(false); }
  };
  const off = async () => {
    setBusy(true);
    try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); setSub(null); toast.success(L("Alerts turned off", "अलर्ट बंद")); }
    catch { toast.error(L("Could not turn off alerts", "अलर्ट बंद नहीं हो सके")); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-wrap items-center gap-4 justify-between" data-testid="parent-alerts">
      <div className="flex gap-3 items-start">
        <Bell className="w-6 h-6 text-[#FF2E63] shrink-0" />
        <div>
          <div className="font-bold text-[#0A192F]">{L("Phone alerts", "फ़ोन अलर्ट")}</div>
          <p className="text-sm text-slate-600">{L("Get a notification when attendance is marked, project photos are added or a certificate is issued.", "हाज़िरी लगने, प्रोजेक्ट फ़ोटो जुड़ने या प्रमाणपत्र जारी होने पर सूचना पाएँ।")}</p>
          {iosNeedsInstall && <p className="text-xs text-amber-700 mt-1" data-testid="alerts-ios-hint">{L("On iPhone: tap Share → “Add to Home Screen”, open RoboSpark from there, then turn on alerts.", "iPhone पर: शेयर → “Add to Home Screen” दबाएँ, वहाँ से RoboSpark खोलें, फिर अलर्ट चालू करें।")}</p>}
          {!supported() && !iosNeedsInstall && <p className="text-xs text-slate-500 mt-1" data-testid="alerts-unsupported">{L("This browser doesn't support notifications.", "यह ब्राउज़र सूचनाएँ सपोर्ट नहीं करता।")}</p>}
        </div>
      </div>
      {sub ? (
        <Button variant="outline" onClick={off} disabled={busy} className="rounded-full" data-testid="alerts-off-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><BellOff className="w-4 h-4 mr-1" />{L("Turn off alerts", "अलर्ट बंद करें")}</>}
        </Button>
      ) : (
        <Button onClick={on} disabled={busy || !supported()} className="rs-cta rounded-full" data-testid="alerts-on-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Bell className="w-4 h-4 mr-1" />{L("Turn on alerts", "अलर्ट चालू करें")}</>}
        </Button>
      )}
    </div>
  );
};
